import React, { useId } from "react";
import { extendTailwindMerge } from "tailwind-merge";
import { cx } from "../../utils/classNames";
import { type ComponentFormatter, formatComponentValue } from "../../utils/formatting";
import { typeRole, typographyRole } from "../../utils/typography";
import { Badge } from "../Badge";
import { Icon } from "../Icon";
import { Skeleton } from "../Skeleton";

// Semantic type roles are font sizes, not text colors. The default merger
// treats unknown text-* classes as colors and drops the role when adding a tone.
const twMerge = extendTailwindMerge({
  extend: { classGroups: { "font-size": Object.values(typographyRole) } },
});

// Use the centralized formatter type
export type KpiFormatter = ComponentFormatter;

/**
 * Kpi Component — Single metric display
 *
 * Displays a key performance indicator with value, delta, trend, and status.
 * Designed to work standalone or within a KpiGroup for dashboard layouts.
 *
 * Usage:
 * ```tsx
 * <Kpi
 *   label="Active Sites"
 *   value={2481}
 *   delta={3.2}
 *   deltaPeriodLabel="vs last 7d"
 *   trend={dataPoints}
 * />
 * ```
 *
 * The "tile" opt-ins (`unit`, `sub`, `dashed`, `sparkVariant="area"`,
 * `tileValueSize`) let the `entity.now` and `metrics.kpi`
 * blocks draw their KPI cell from this one primitive instead of hand-rolling it.
 * Every one is optional and defaults to the legacy presentation, so existing
 * callers (Boards, standalone Kpi) are unaffected until they opt in.
 */

// ——————————————————————————————————————————
// Types
// ——————————————————————————————————————————

export type KpiStatus = "neutral" | "success" | "warning" | "error" | "info";
export type KpiSize = "sm" | "md" | "lg";
export type KpiOrientation = "vertical" | "horizontal";

export interface TrendPoint {
  x: number | string | Date;
  y: number;
}

export interface KpiProps {
  /** Short label describing the metric */
  label: React.ReactNode;
  /** Primary value - number for auto-formatting or string for custom display */
  value: number | string;
  /** Formatter using the unified formatting system */
  formatter?: KpiFormatter;
  /** Change since prior period */
  delta?: number;
  /** Description of delta period (e.g., "vs last 7d") */
  deltaPeriodLabel?: string;
  /** How to interpret delta direction */
  deltaIntent?: "upIsGood" | "downIsGood";
  /** Mini trend data points */
  trend?: TrendPoint[];
  /** Status indicator */
  status?: {
    value: string;
    tone?: KpiStatus;
  };
  /** Help text or tooltip content */
  helpText?: React.ReactNode;
  /** Size variant — controls label/value type roles and internal gap. */
  size?: KpiSize;
  /** Layout orientation */
  orientation?: KpiOrientation;
  /** Loading state */
  isLoading?: boolean;
  /** Empty state */
  empty?: boolean;
  /** Error state */
  error?: string | React.ReactNode;
  /** Click handler for drill-down */
  onClick?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Additional CSS classes for the label element (e.g. a mono-caps caption). */
  labelClassName?: string;
  /** Label placement relative to the value. In horizontal orientation, below means after the value. */
  labelPosition?: "above" | "below";
  /** Additional classes for the value output, mirroring labelClassName. */
  valueClassName?: string;
  /** Custom value renderer */
  renderValue?: (value: KpiProps["value"]) => React.ReactNode;
  /** Custom delta renderer */
  renderDelta?: (delta?: number) => React.ReactNode;
  /** Custom trend renderer */
  renderTrend?: (points?: TrendPoint[]) => React.ReactNode;

  // ——— Tile opt-ins (shared by the entity.now / metrics.kpi blocks) ———
  /**
   * Secondary unit suffix rendered inline after the value in a smaller,
   * secondary tone (e.g. "kW", "%", "MW"). Display-only.
   */
  unit?: string;
  /**
   * Secondary line under the value (e.g. "as of 2h ago", "of 400 A"). Static,
   * already localised by the caller. Kept even when `dashed`.
   */
  sub?: React.ReactNode;
  /**
   * Offline contract: the value is dashed to "—" and de-emphasised (the entity
   * is offline and this is a live metric). The cell keeps its slot so a grid
   * stays identical online/offline; the sparkline is suppressed and `sub` is
   * kept. Distinct from `empty` (which is "no metric at all").
   */
  dashed?: boolean;
  /**
   * Sparkline style. `"line"` (default, back-compat) is the thin border-muted
   * polyline; `"area"` is the smooth Catmull-Rom curve with a gradient area
   * fill (the shared tile sparkline). The caller decides whether a series is
   * worth drawing; this only draws for ≥ 2 points and no-ops otherwise.
   */
  sparkVariant?: "line" | "area";
  /** Text-color utility class for the `"area"` sparkline (drives currentColor). */
  sparkTone?: string;
  /**
   * Opt-in "tile" value presentation: brand-font display value at a fixed size.
   * `"md"` = 24px (entity/device tiles), `"lg"` = 34px (Boards summary). When
   * unset the value uses the `size`-driven display type role (legacy behavior).
   */
  tileValueSize?: "md" | "lg";
}

// ——————————————————————————————————————————
// Tile presentation constants
// ——————————————————————————————————————————

// One-off display sizes for the KPI tile with no matching type-scale token, so
// they live here as explicit values; the brand family is pulled from the
// `--font-family-brand` token so it re-themes. `md` matches the device
// entity.now tile (24px), `lg` matches the Boards summary headline (34px).
const TILE_VALUE_STYLE: Record<"md" | "lg", React.CSSProperties> = {
  md: { fontFamily: "var(--font-family-brand)", fontSize: 24, lineHeight: 1, letterSpacing: "-0.02em" },
  lg: { fontFamily: "var(--font-family-brand)", fontSize: 34, lineHeight: 1.1 },
};
const TILE_UNIT_STYLE: Record<"md" | "lg", React.CSSProperties> = {
  md: { fontSize: 12 },
  lg: { fontSize: 16 },
};
const TILE_SUB_STYLE: React.CSSProperties = { fontSize: 11 };

// ——————————————————————————————————————————
// Helpers
// ——————————————————————————————————————————

// Use the centralized formatting utility
function formatValue(value: number | string, formatter?: KpiFormatter): React.ReactNode {
  return formatComponentValue({
    value,
    formatter,
    emptyClassName: "text-text-secondary",
    emptyText: "—",
  });
}

/**
 * The delta's tone, as the same tokens `DeltaCell` uses.
 *
 * `-text`, not the bare `text-feedback-success` this carried until MR-08: the feedback
 * tokens come as a `{text,background,border}` triple and there is no
 * `--color-feedback-success`, so the short spelling generated no utility and the delta
 * simply inherited the tile's colour. One spelling across the design system, and the
 * green and red now actually render.
 */
function getDeltaColor(delta: number, deltaIntent: "upIsGood" | "downIsGood" = "upIsGood"): string {
  if (delta === 0) return "text-text-secondary";

  const isPositive = delta > 0;

  if (deltaIntent === "upIsGood") {
    return isPositive ? "text-feedback-success-text" : "text-feedback-error-text";
  } else {
    return isPositive ? "text-feedback-error-text" : "text-feedback-success-text";
  }
}

// ——————————————————————————————————————————
// Sparkline Components
// ——————————————————————————————————————————

function Sparkline({ points, className, ariaLabel }: { points: TrendPoint[]; className?: string; ariaLabel?: string }) {
  const width = 64;
  const height = 20;
  const padding = 2;

  // Extract y values
  const yValues = points.map((p) => (typeof p.y === "number" ? p.y : 0));

  if (yValues.length === 0) return null;

  // Calculate bounds
  const min = Math.min(...yValues);
  const max = Math.max(...yValues);
  const range = max - min;

  // Normalize y coordinate
  const normalizeY = (y: number) => {
    if (range === 0) return height / 2;
    return height - padding - ((y - min) / range) * (height - 2 * padding);
  };

  // Calculate x step
  const xStep = (width - 2 * padding) / Math.max(points.length - 1, 1);

  // Build path
  const pathData = points
    .map((point, index) => {
      const x = padding + index * xStep;
      const y = normalizeY(typeof point.y === "number" ? point.y : 0);
      return `${index === 0 ? "M" : "L"} ${x} ${y}`;
    })
    .join(" ");

  return (
    <svg
      width={width}
      height={height}
      role="img"
      aria-label={ariaLabel}
      className={twMerge("overflow-visible", className)}
    >
      <path d={pathData} fill="none" strokeWidth="1.5" className="stroke-border-muted" />
    </svg>
  );
}

// Area-sparkline geometry, in an SVG user-space stretched to the cell width via
// `preserveAspectRatio="none"` (marks use `vector-effect:non-scaling-stroke` so
// the line stays a crisp 2px however wide the cell is).
const SPARK_W = 100;
const SPARK_H = 30;
const SPARK_PAD_X = 1.5;
const SPARK_PAD_Y = 4;
// Down-sample a noisy series to a calm ~12-point curve so the sparkline reads as
// a trend, not an EKG. Keeps the first and last samples.
const SPARK_TARGET_POINTS = 12;

/** Evenly-spaced down-sample to at most `target` points, always keeping the ends. */
function downsample(points: TrendPoint[], target: number): TrendPoint[] {
  if (points.length <= target) return points;
  const stepD = (points.length - 1) / (target - 1);
  const out: TrendPoint[] = [];
  for (let i = 0; i < target; i++) out.push(points[Math.round(i * stepD)]);
  return out;
}

/**
 * A smooth (Catmull-Rom → cubic-Bézier) path through `pts`. The rounded curve is
 * what turns a hard `L`-segment polyline into a soft trend; the tension is the
 * standard 1/6 Catmull-Rom factor, and the endpoints are duplicated so the
 * first/last segments curve naturally instead of kinking.
 */
function smoothLinePath(pts: ReadonlyArray<readonly [number, number]>): string {
  if (pts.length < 2) return pts.length === 1 ? `M ${pts[0][0]} ${pts[0][1]}` : "";
  let d = `M ${pts[0][0]} ${pts[0][1]}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const cp1x = p1[0] + (p2[0] - p0[0]) / 6;
    const cp1y = p1[1] + (p2[1] - p0[1]) / 6;
    const cp2x = p2[0] - (p3[0] - p1[0]) / 6;
    const cp2y = p2[1] - (p3[1] - p1[1]) / 6;
    d += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${p2[0]} ${p2[1]}`;
  }
  return d;
}

/**
 * The shared tile sparkline (`sparkVariant="area"`): a smooth, soft-toned area
 * curve, not a hard polyline. Full cell width, ~30px tall, in the caller's
 * `sparkTone` at full stroke with a gradient area fill fading to transparent
 * (per the dataviz single-series guidance). `gradientId` is instance-unique (a
 * `useId` in the Kpi) so multiple cells on a page never collide on the `<defs>`
 * gradient id. No-ops on an empty/degenerate series (< 2 points).
 */
function AreaSparkline({
  points,
  gradientId,
  tone,
  className,
}: {
  points: TrendPoint[];
  gradientId: string;
  tone?: string;
  className?: string;
}) {
  if (!points || points.length < 2) return null;
  const sampled = downsample(points, SPARK_TARGET_POINTS);
  const ys = sampled.map((p) => (typeof p.y === "number" ? p.y : 0));
  const min = Math.min(...ys);
  const max = Math.max(...ys);
  const range = max - min;
  const ny = (y: number) =>
    range === 0 ? SPARK_H / 2 : SPARK_H - SPARK_PAD_Y - ((y - min) / range) * (SPARK_H - 2 * SPARK_PAD_Y);
  const step = (SPARK_W - 2 * SPARK_PAD_X) / Math.max(sampled.length - 1, 1);
  const pts = sampled.map((p, i) => [SPARK_PAD_X + i * step, ny(typeof p.y === "number" ? p.y : 0)] as const);
  const line = smoothLinePath(pts);
  const last = pts[pts.length - 1];
  const first = pts[0];
  const area = `${line} L ${last[0]} ${SPARK_H} L ${first[0]} ${SPARK_H} Z`;
  return (
    <svg
      width="100%"
      height={SPARK_H}
      viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="Trend"
      className={twMerge("mt-2 block w-full", tone, className)}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="currentColor" stopOpacity={0.22} />
          <stop offset="100%" stopColor="currentColor" stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gradientId})`} stroke="none" />
      <path
        d={line}
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
}

// ——————————————————————————————————————————
// Main Component
// ——————————————————————————————————————————

export function Kpi({
  label,
  value,
  formatter,
  delta,
  deltaPeriodLabel,
  deltaIntent = "upIsGood",
  trend,
  status,
  helpText,
  size = "md",
  orientation = "vertical",
  isLoading,
  empty,
  error,
  onClick,
  className,
  labelClassName,
  labelPosition = "above",
  valueClassName,
  renderValue,
  renderDelta,
  renderTrend,
  unit,
  sub,
  dashed,
  sparkVariant = "line",
  sparkTone,
  tileValueSize,
}: KpiProps) {
  // Instance-unique id so multiple area sparklines never share a gradient def.
  const areaGradientId = useId();
  const labelId = `${areaGradientId}-label`;

  // Size classes map component scale to semantic typography roles.
  const sizeClasses = {
    sm: {
      label: typeRole("labelSm"),
      value: typeRole("displaySm"),
      delta: typeRole("labelSm"),
      gap: "gap-1",
    },
    md: {
      label: typeRole("labelMd"),
      value: typeRole("displayMd"),
      delta: typeRole("bodySm"),
      gap: "gap-2",
    },
    lg: {
      label: typeRole("labelLg"),
      value: typeRole("displayLg"),
      delta: typeRole("bodyMd"),
      gap: "gap-3",
    },
  };

  const currentSize = sizeClasses[size];
  // The value + optional unit suffix, rendered in the "tile" brand-font style.
  const tileValueNode = (raw: React.ReactNode): React.ReactNode =>
    tileValueSize ? (
      <span style={TILE_VALUE_STYLE[tileValueSize]} className={dashed ? "opacity-45" : undefined}>
        {raw}
        {unit ? (
          <span className="text-text-secondary" style={TILE_UNIT_STYLE[tileValueSize]}>
            {" "}
            {unit}
          </span>
        ) : null}
      </span>
    ) : (
      <>
        {raw}
        {unit ? <span className="text-text-secondary"> {unit}</span> : null}
      </>
    );

  // Format value
  const valueNode = isLoading ? (
    <Skeleton className="h-7 w-20" />
  ) : empty ? (
    <span className="text-text-secondary">—</span>
  ) : error ? (
    <span className={typeRole("bodySm", "text-feedback-error")}>{typeof error === "string" ? error : "Error"}</span>
  ) : dashed ? (
    tileValueNode("—")
  ) : renderValue ? (
    renderValue(value)
  ) : tileValueSize ? (
    tileValueNode(value)
  ) : (
    tileValueNode(formatValue(value, formatter))
  );

  // Format delta
  const deltaNode = renderDelta ? (
    renderDelta(delta)
  ) : delta !== undefined && !isLoading && !empty && !error && !dashed ? (
    <div className={cx(currentSize.delta, getDeltaColor(delta, deltaIntent), "flex items-center gap-1")}>
      {delta > 0 && <Icon name="ArrowUp" size={12} />}
      {delta < 0 && <Icon name="ArrowDown" size={12} />}
      {delta === 0 && <Icon name="Minus" size={12} />}
      <span>
        {delta > 0 ? "+" : ""}
        {Math.abs(delta).toFixed(1)}%
      </span>
      {deltaPeriodLabel && <span className="text-text-secondary">· {deltaPeriodLabel}</span>}
    </div>
  ) : isLoading && delta !== undefined ? (
    <Skeleton className="h-4 w-24" />
  ) : null;

  // Trend node — hidden while dashed (offline) so no misleading flat line shows.
  const trendNode = renderTrend ? (
    renderTrend(trend)
  ) : trend && trend.length > 0 && !isLoading && !error && !dashed ? (
    sparkVariant === "area" ? (
      <AreaSparkline
        points={trend}
        gradientId={areaGradientId}
        tone={sparkTone}
        className={empty ? "opacity-30" : ""}
      />
    ) : (
      <Sparkline points={trend} ariaLabel={`${label} trend`} className={empty ? "opacity-30" : ""} />
    )
  ) : isLoading && trend !== undefined ? (
    <Skeleton className="h-5 w-16" />
  ) : null;

  const subNode =
    sub && !isLoading && !error ? (
      <div className="text-text-secondary" style={TILE_SUB_STYLE}>
        {sub}
      </div>
    ) : null;

  const labelNode = (
    <div className="flex items-center gap-2">
      <h3 id={labelId} className={twMerge(cx(currentSize.label, "text-text-secondary truncate"), labelClassName)}>
        {isLoading ? <Skeleton className="h-4 w-24" /> : label}
      </h3>
      {helpText && !isLoading && (
        <Icon name="Question" size={12} className="text-text-secondary cursor-help" aria-label="Help" />
      )}
    </div>
  );

  return (
    <section
      className={twMerge(
        "flex min-w-0",
        orientation === "horizontal" ? `items-center ${currentSize.gap}` : `flex-col ${currentSize.gap}`,
        onClick && "cursor-pointer hover:bg-background-muted rounded-lg px-3 py-2 -mx-3 -my-2 transition-colors",
        className
      )}
      aria-labelledby={labelId}
      onClick={onClick}
    >
      {labelPosition === "above" && labelNode}

      {/* Value and status */}
      <div className="flex items-baseline gap-2">
        <output
          className={twMerge(
            tileValueSize ? undefined : currentSize.value,
            error ? "text-feedback-error" : "text-text-primary",
            valueClassName
          )}
          aria-label={`Value: ${value}`}
        >
          {valueNode}
        </output>
        {status && !isLoading && !error && (
          <Badge variant={status.tone || "neutral"} size="sm">
            {status.value}
          </Badge>
        )}
      </div>

      {labelPosition === "below" && labelNode}

      {/* Secondary line */}
      {subNode}

      {/* Delta and trend row */}
      {(deltaNode || trendNode) && (
        <div className="flex items-center gap-3">
          {deltaNode}
          {trendNode}
        </div>
      )}
    </section>
  );
}
